<template>
  <section id="hero" class="relative bg-brand-blue">
    <div class="max-w-screen-xl px-5 mx-auto" id="form-step-1">
      <div class="mx-auto pt-8 pb-10 lg:pt-16 lg:pb-20">
        <div class="relative font-division-heading text-center">
          <span class="text-2xl lg:text-4xl font-thin text-white">Find a</span><br>
          <h1 class="mt-4 text-4xl lg:text-7xl font-bold text-center text-white">
            Affordable {{ sectorText }} Lawyer<br>
            <span v-if="locationText" class="block mt-2">{{ locationText }}</span>
          </h1>
        </div>

        <div class="mt-5 lg:mt-10">
          <ul class="flex flex-col items-start sm:items-center sm:flex-row sm:flex-wrap sm:justify-center border-blue-500 space-y-3 md:space-y-0 sm:space-x-8">
            <li v-for="(bullet, index) in bullets" :key="index" class="flex justify-center items-center font-medium text-white">
              <svg class="text-green-500 mr-3 flex-none" width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M25.6663 12.9262V13.9995C25.6649 16.5153 24.8503 18.9633 23.3439 20.9783C21.8375 22.9933 19.7202 24.4674 17.3076 25.1807C14.895 25.8941 12.3165 25.8084 9.95656 24.9365C7.59664 24.0647 5.58177 22.4533 4.21246 20.3428C2.84315 18.2322 2.19276 15.7356 2.3583 13.2252C2.52383 10.7148 3.49642 8.32523 5.131 6.41277C6.76559 4.50031 8.97459 3.16746 11.4286 2.61302C13.8825 2.05858 16.45 2.31224 18.748 3.33618" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M25.6667 4.66699L14 16.3453L10.5 12.8453" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {{ bullet }}
            </li>
          </ul>
        </div>

        <div class="flex flex-col mx-auto mt-4">
          <!-- Modal overlay -->
          <div 
            v-if="isFormFocused" 
            class="fixed inset-0 bg-white opacity-75 z-40 transition-opacity backdrop-filter backdrop-blur-sm"
            @click="closeFormFocus"
          ></div>
          
          <!-- IntakeFormWrapper integration -->
          <div 
            class="max-w-3xl w-full mx-auto relative will-change-transform"
            :class="{'z-50': isFormFocused}"
          >
            <div 
              class="transition-all transform duration-300 will-change-transform"
              :class="{
                'shadow-2xl rounded-lg bg-white bg-opacity-95 p-4': isFormFocused,
                'scale-105': isFormFocused
              }"
            >
              <!-- Close button -->
              <button 
                v-if="isFormFocused" 
                @click="closeFormFocus" 
                class="absolute top-2 right-2 text-gray-500 hover:text-gray-800 z-50"
                aria-label="Close form"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <IntakeFormWrapper
                :api-url="apiUrl"
                :api-token="apiToken"
                :api-headers="apiHeaders"
                @jurisdiction-selection-submitted="onJurisdictionSelected"
                @issue-submitted="onIssueSubmitted"
                @contact-details-submitted="onContactDetailsSubmitted"
                @intake-complete="onIntakeComplete"
                @issue-text-updated="onIssueTextUpdated"
              />
            </div>
          </div>
        </div>

        <h2 class="mt-8 lg:mt-5 text-center text-white font-light leading-loose lg:leading-none">
          60 Seconds Could <strong class="font-semibold">Save You Up To 80% On Upfront Legal Fees</strong>
        </h2>
      </div>
    </div>
  </section>
</template>

<script>
import IntakeFormWrapper from './IntakeFormWrapper.vue';

export default {
  components: {
    IntakeFormWrapper,
  },

  props: {
    bullets: {
      type: Array,
      default: () => []
    },
    sectorText: {
      type: String,
      default: 'Family Law'
    },
    locationText: {
      type: String,
      default: ''
    },
    backgroundImage: {
      type: String,
      default: null
    },
    dynamic: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      apiUrl: process.env.MIX_INTAKE_API_URL || 'http://al-client-intake.test/api/v1',
      apiToken: process.env.MIX_INTAKE_API_TOKEN || 'api-token',
      apiHeaders: {},
      isFormFocused: false
    };
  },

  computed: {
    hasBackgroundImage() {
      return this.backgroundImage || (this.dynamic && this.dynamic.img && this.dynamic.img.background);
    },
    
    backgroundImageUrl() {
      return this.backgroundImage || (this.dynamic && this.dynamic.img && this.dynamic.img.background);
    }
  },

  mounted() {
    // Track page view
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'form_start',
        form_id: 'ulh-lead-intake-alternative',
        form_name: 'ULH Lead Intake Alternative'
      });
    }
    
    // Apply background image if available
    if (this.hasBackgroundImage) {
      const heroElement = document.getElementById('hero');
      if (heroElement) {
        const beforeStyle = window.getComputedStyle(heroElement, '::before');
        if (beforeStyle) {
          heroElement.style.setProperty('--background-image', `url('${this.backgroundImageUrl}')`);
        }
      }
    }
    
    // Add event listener for escape key to close modal
    document.addEventListener('keydown', this.handleKeyDown);
  },
  
  beforeDestroy() {
    // Remove event listener when component is destroyed
    document.removeEventListener('keydown', this.handleKeyDown);
  },

  methods: {
    onJurisdictionSelected(detail) {
      console.log('Jurisdiction selected:', detail);
      this.$emit('jurisdiction-selected', detail);
    },

    onIssueSubmitted(detail) {
      console.log('Issue submitted:', detail);
      this.$emit('issue-submitted', detail);
    },

    onContactDetailsSubmitted(detail) {
      console.log('Contact details submitted:', detail);
      this.$emit('contact-details-submitted', detail);
    },

    onIntakeComplete(detail) {
      console.log('Intake complete:', detail);
      this.$emit('intake-complete', detail);
      
      // Track completion
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'form_complete',
          form_id: 'ulh-lead-intake-alternative',
          form_name: 'ULH Lead Intake Alternative'
        });
      }
    },
    
    onIssueTextUpdated(detail) {
      // Activate focus mode when text is entered
      if (detail && detail.text && detail.text.length > 0) {
        this.isFormFocused = true;
      }
    },
    
    closeFormFocus() {
      this.isFormFocused = false;
    },
    
    handleKeyDown(event) {
      // Close modal on escape key
      if (event.key === 'Escape' && this.isFormFocused) {
        this.closeFormFocus();
      }
    }
  }
};
</script>

<style scoped>
/* Match the original background styling */
#hero {
  position: relative;
}

#hero::before {
  content: '';
  background-color: #005298;
  background-position: 45% 15%;
  background-size: 400%;
  background-repeat: no-repeat;
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: -1;
  background-image: var(--background-image, none);
}

@media screen and (min-width: 768px) {
  #hero::before {
    background-position: center center;
    background-size: cover;
  }
}

/* Modal animations */
.fixed {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 0.75; }
}

/* Performance optimizations */
.will-change-transform {
  will-change: transform;
  backface-visibility: hidden;
  perspective: 1000px;
}
</style> 